import { useState, useEffect } from "react";
import { supabase } from "../supabaseClient";

export default function FinancePage({ cabinetId = null }) {
  const [entries, setEntries] = useState([]);
  const [projects, setProjects] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [schedule, setSchedule] = useState([]);

  const [projectId, setProjectId] = useState("");
  const [type, setType] = useState("income");
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [category, setCategory] = useState("");
  const [date, setDate] = useState("");
  const [installmentNumber, setInstallmentNumber] = useState("");

  useEffect(() => {
    if (cabinetId == null) return;
    loadAll();
  }, [cabinetId]);

  async function loadAll() {
    // 1. Charger les projets du cabinet (projects a bien cabinet_id)
    const { data: projectsData } = await supabase
      .from("projects")
      .select("id,name")
      .eq("cabinet_id", cabinetId);

    const projectList = projectsData || [];
    setProjects(projectList);
    const ids = projectList.map((p) => p.id);

    // 2. Charger milestones via cabinet_id (colonne existante sur milestones)
    const milestonesPromise = supabase
      .from("milestones")
      .select("*")
      .eq("cabinet_id", cabinetId);

    // 3. finance et payment_schedule n'ont PAS cabinet_id — filtrer par project_id
    const financePromise =
      ids.length > 0
        ? supabase.from("finance").select("*").in("project_id", ids)
        : Promise.resolve({ data: [] });

    const schedulePromise =
      ids.length > 0
        ? supabase.from("payment_schedule").select("*").in("project_id", ids)
        : Promise.resolve({ data: [] });

    const [milestonesRes, financeRes, scheduleRes] = await Promise.all([
      milestonesPromise,
      financePromise,
      schedulePromise,
    ]);

    if (milestonesRes.data) setMilestones(milestonesRes.data);
    if (financeRes.data) setEntries(financeRes.data);
    if (scheduleRes.data) setSchedule(scheduleRes.data);
  }

  async function loadEntries() {
    const { data: proj } = await supabase.from("projects").select("id").eq("cabinet_id", cabinetId);
    const ids = (proj || []).map((p) => p.id);
    if (ids.length === 0) { setEntries([]); return; }
    const { data } = await supabase.from("finance").select("*").in("project_id", ids);
    if (data) setEntries(data);
  }

  function echeanceAutorisee(projectId, installmentNumber) {
    if (!installmentNumber) return false;
  
    const jalonCorrespondant = milestones.find(
      (m) =>
        String(m.project_id) === String(projectId) &&
        Number(m.number) === Number(installmentNumber)
    );
  
    if (!jalonCorrespondant) return false;
  
    return jalonCorrespondant.status === "done";
  }

  function getProjectName(projectId) {
    const projet = projects.find((p) => String(p.id) === String(projectId));
    return projet ? projet.name : "Projet inconnu";
  }

  function getProjectInstallments(projectId) {
    return schedule.filter(
      (row) => String(row.project_id) === String(projectId)
    );
  }

  async function addEntry() {
    if (!projectId || !amount) {
      alert("Projet et montant obligatoires");
      return;
    }

    if (!installmentNumber) {
      alert("Choisis une échéance concernée.");
      return;
    }
    
    if (!echeanceAutorisee(projectId, installmentNumber)) {
      alert("Paiement bloqué : le jalon correspondant à cette échéance n'est pas validé.");
      return;
    }

    const payload = {
      project_id: projectId,
      type: type,
      description: description,
      amount: parseFloat(amount),
      transaction_date: date || new Date().toISOString().split("T")[0],
      category: category,
      installment_number: installmentNumber ? Number(installmentNumber) : null,
    };

    const { error } = await supabase
      .from("finance")
      .insert([payload]);

      if (error) {
        console.error("Erreur Supabase complète :", error);
        alert("Erreur insertion : " + (error.message || "message inconnu"));
      } else {
      loadEntries();
      setDescription("");
      setAmount("");
      setCategory("");
      setDate("");
      setInstallmentNumber("");
    }
  }

  function formatMoney(value) {
    return Number(value || 0).toLocaleString("fr-FR") + " MAD";
  }

  function getTypeBadgeStyle(entryType) {
    if (entryType === "income") {
      return {
        background: "rgba(92,138,92,0.14)",
        color: "#5C8A5C",
        border: "1px solid rgba(92,138,92,0.42)",
      };
    }
    return {
      background: "rgba(184,92,58,0.14)",
      color: "#B85C3A",
      border: "1px solid rgba(184,92,58,0.42)",
    };
  }

  const totalEntrees = entries
    .filter((e) => e.type === "income")
    .reduce((sum, e) => sum + Number(e.amount || 0), 0);
  const totalSorties = entries
    .filter((e) => e.type === "expense")
    .reduce((sum, e) => sum + Number(e.amount || 0), 0);
  const soldeNet = totalEntrees - totalSorties;
  const nbOperations = entries.length;

  if (cabinetId == null) {
    return (
      <main style={{ minHeight: "100vh", background: "#F8F4EE", padding: 24 }}>
        <div style={{ textAlign: "center", fontFamily: "'DM Sans', sans-serif", color: "#8A7664" }}>
          Chargement...
        </div>
      </main>
    );
  }

  return (
    <main style={{ minHeight: "100vh", background: "#F8F4EE", padding: 24 }}><style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@500;700&family=DM+Sans:wght@400;500;700&display=swap');
      `}</style><header style={{ marginBottom: 16 }}><h1 style={{ margin: 0, fontFamily: "'Playfair Display', serif", color: "#3D2E20", fontSize: 34 }}>
          Finance
        </h1></header><section
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
          gap: 12,
          marginBottom: 18,
        }}
      ><div style={{ background: "#FFFFFF", border: "1px solid #D4E7D4", borderRadius: 12, padding: 14 }}><div style={{ fontFamily: "'DM Sans', sans-serif", color: "#8A7664", fontSize: 12, marginBottom: 8 }}>Total Entrées (MAD)</div><div style={{ fontFamily: "'Playfair Display', serif", fontSize: 28, color: "#5C8A5C", fontWeight: 700 }}>{formatMoney(totalEntrees)}</div></div><div style={{ background: "#FFFFFF", border: "1px solid #E9CFC6", borderRadius: 12, padding: 14 }}><div style={{ fontFamily: "'DM Sans', sans-serif", color: "#8A7664", fontSize: 12, marginBottom: 8 }}>Total Sorties (MAD)</div><div style={{ fontFamily: "'Playfair Display', serif", fontSize: 28, color: "#B85C3A", fontWeight: 700 }}>{formatMoney(totalSorties)}</div></div><div style={{ background: "#FFFFFF", border: "1px solid #E3D7C8", borderRadius: 12, padding: 14 }}><div style={{ fontFamily: "'DM Sans', sans-serif", color: "#8A7664", fontSize: 12, marginBottom: 8 }}>Solde net (MAD)</div><div style={{ fontFamily: "'Playfair Display', serif", fontSize: 28, color: soldeNet >= 0 ? "#5C8A5C" : "#B85C3A", fontWeight: 700 }}>
            {formatMoney(soldeNet)}
          </div></div><div style={{ background: "#FFFFFF", border: "1px solid #E3D7C8", borderRadius: 12, padding: 14 }}><div style={{ fontFamily: "'DM Sans', sans-serif", color: "#8A7664", fontSize: 12, marginBottom: 8 }}>Nombre d'opérations</div><div style={{ fontFamily: "'Playfair Display', serif", fontSize: 28, color: "#C09A5B", fontWeight: 700 }}>{nbOperations}</div></div></section><section style={{ background: "#FFFFFF", border: "1px solid #E3D7C8", borderRadius: 12, padding: 16, marginBottom: 18 }}><div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, gap: 12, flexWrap: "wrap" }}><h2 style={{ margin: 0, fontFamily: "'Playfair Display', serif", color: "#3D2E20", fontSize: 26 }}>
            Nouvelle opération
          </h2><button
            onClick={addEntry}
            style={{
              border: "none",
              background: "#C09A5B",
              color: "#FFFFFF",
              borderRadius: 8,
              padding: "10px 18px",
              cursor: "pointer",
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 12,
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              fontWeight: 700,
            }}
          >
            Ajouter
          </button></div><div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 12 }}><div><label style={{ display: "block", fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: "#8A7664", marginBottom: 6 }}>Projet</label><select
              value={projectId}
              onChange={(e) => {
                setProjectId(e.target.value);
                setInstallmentNumber("");
              }}
              style={{ width: "100%", border: "1px solid #D8C9B8", borderRadius: 8, padding: "10px 12px", background: "#FFF", fontFamily: "'DM Sans', sans-serif" }}
            ><option value="">Projet</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select></div><div><label style={{ display: "block", fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: "#8A7664", marginBottom: 6 }}>Type</label><select value={type} onChange={(e) => setType(e.target.value)} style={{ width: "100%", border: "1px solid #D8C9B8", borderRadius: 8, padding: "10px 12px", background: "#FFF", fontFamily: "'DM Sans', sans-serif" }}><option value="income">Entrée</option><option value="expense">Sortie</option></select></div><div><label style={{ display: "block", fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: "#8A7664", marginBottom: 6 }}>Description</label><input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Description" style={{ width: "100%", border: "1px solid #D8C9B8", borderRadius: 8, padding: "10px 12px", fontFamily: "'DM Sans', sans-serif" }} /></div><div><label style={{ display: "block", fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: "#8A7664", marginBottom: 6 }}>Montant (MAD)</label><input value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Montant" style={{ width: "100%", border: "1px solid #D8C9B8", borderRadius: 8, padding: "10px 12px", fontFamily: "'DM Sans', sans-serif" }} /></div><div><label style={{ display: "block", fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: "#8A7664", marginBottom: 6 }}>Catégorie</label><select value={category} onChange={(e) => setCategory(e.target.value)} style={{ width: "100%", border: "1px solid #D8C9B8", borderRadius: 8, padding: "10px 12px", background: "#FFF", fontFamily: "'DM Sans', sans-serif" }}><option value="">Catégorie</option><option value="vente">Vente</option><option value="honoraires">Honoraires YOMA</option><option value="terrain">Achat terrain</option><option value="gros_oeuvre">Gros œuvre</option><option value="second_oeuvre">Second œuvre</option><option value="vrd">VRD</option><option value="frais_admin">Frais administratifs</option></select></div><div><label style={{ display: "block", fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: "#8A7664", marginBottom: 6 }}>Échéance</label><select value={installmentNumber} onChange={(e) => setInstallmentNumber(e.target.value)} style={{ width: "100%", border: "1px solid #D8C9B8", borderRadius: 8, padding: "10px 12px", background: "#FFF", fontFamily: "'DM Sans', sans-serif" }}><option value="">Échéance concernée</option>
              {getProjectInstallments(projectId).map((row) => (
                <option key={row.id} value={row.installment_number}>
                  V{row.installment_number} — {row.label}
                </option>
              ))}
            </select></div><div style={{ gridColumn: "1 / -1", maxWidth: 300 }}><label style={{ display: "block", fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: "#8A7664", marginBottom: 6 }}>Date</label><input type="date" value={date} onChange={(e) => setDate(e.target.value)} style={{ width: "100%", border: "1px solid #D8C9B8", borderRadius: 8, padding: "10px 12px", fontFamily: "'DM Sans', sans-serif" }} /></div></div>

        {projectId && installmentNumber && (
          <p style={{ marginTop: 12, fontFamily: "'DM Sans', sans-serif", fontSize: 12, color: "#8A7664" }}>
            Autorisation finance :{" "}
            <strong style={{ color: echeanceAutorisee(projectId, installmentNumber) ? "#5C8A5C" : "#B85C3A" }}>
              {echeanceAutorisee(projectId, installmentNumber) ? "Oui" : "Non"}
            </strong></p>
        )}
      </section><section style={{ background: "#FFFFFF", border: "1px solid #E3D7C8", borderRadius: 12, padding: 14 }}><h3 style={{ margin: "0 0 10px", fontFamily: "'Playfair Display', serif", color: "#3D2E20", fontSize: 24 }}>
          Historique financier
        </h3><table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}><thead><tr style={{ background: "#F5EFE6" }}><th style={{ textAlign: "left", padding: "10px 12px", fontFamily: "'DM Sans', sans-serif", fontSize: 12, color: "#8A7664" }}>Date</th><th style={{ textAlign: "left", padding: "10px 12px", fontFamily: "'DM Sans', sans-serif", fontSize: 12, color: "#8A7664" }}>Projet</th><th style={{ textAlign: "left", padding: "10px 12px", fontFamily: "'DM Sans', sans-serif", fontSize: 12, color: "#8A7664" }}>Type</th><th style={{ textAlign: "left", padding: "10px 12px", fontFamily: "'DM Sans', sans-serif", fontSize: 12, color: "#8A7664" }}>Description</th><th style={{ textAlign: "right", padding: "10px 12px", fontFamily: "'DM Sans', sans-serif", fontSize: 12, color: "#8A7664" }}>Montant</th><th style={{ textAlign: "left", padding: "10px 12px", fontFamily: "'DM Sans', sans-serif", fontSize: 12, color: "#8A7664" }}>Catégorie</th></tr></thead><tbody>
            {entries.map((e, idx) => (
              <tr key={e.id} style={{ background: idx % 2 ? "#FCF9F4" : "#FFFFFF", borderTop: "1px solid #EFE4D4" }}><td style={{ padding: "10px 12px", fontFamily: "'DM Sans', sans-serif", color: "#3D2E20" }}>{e.transaction_date || "-"}</td><td style={{ padding: "10px 12px", fontFamily: "'DM Sans', sans-serif", color: "#3D2E20", fontWeight: 500 }}>{e.projects?.name || getProjectName(e.project_id)}</td><td style={{ padding: "10px 12px" }}><span style={{ display: "inline-block", borderRadius: 999, padding: "4px 10px", fontFamily: "'DM Sans', sans-serif", fontSize: 11, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", ...getTypeBadgeStyle(e.type) }}>
                    {e.type === "income" ? "Entrée" : "Sortie"}
                  </span></td><td style={{ padding: "10px 12px", fontFamily: "'DM Sans', sans-serif", color: "#3D2E20" }}>{e.description || "-"}</td><td style={{ padding: "10px 12px", textAlign: "right", fontFamily: "'DM Sans', sans-serif", fontWeight: 700, color: e.type === "income" ? "#5C8A5C" : "#B85C3A" }}>
                  {formatMoney(e.amount)}
                </td><td style={{ padding: "10px 12px", fontFamily: "'DM Sans', sans-serif", color: "#3D2E20" }}>{e.category || "-"}</td></tr>
            ))}
          </tbody></table></section></main>
  );
}